import React from 'react';
import { Link, NavigateFunction } from 'react-router-dom';
import { openAppRouteInNewWindow } from '../../../shared/utils/openAppRouteInNewWindow';

/**
 * Topbar — habillage repris du prototype (`.topbar`) : 46 px, dégradé sombre,
 * marque compacte à gauche, économie + bascules + compte à droite. La navigation
 * a migré dans le rail latéral (`MapRail.tsx`).
 */

export const TOPBAR_HEIGHT = 46;

interface MapHeaderProps {
  currentUser: {
    username: string;
    argents?: number | null;
    medailles?: number | null;
    roles_id?: number | null;
  };
  headerClock: string;
  showInterventionSectors: boolean;
  setShowInterventionSectors: React.Dispatch<React.SetStateAction<boolean>>;
  priseAppels: boolean;
  priseLoadError: string | null;
  priseToggling: boolean;
  togglePriseAppels: () => Promise<void>;
  navigate: NavigateFunction;
  hasPanelEntryAccess: boolean;
}

const chip: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  borderRadius: 5,
  padding: '3px 8px',
  fontSize: 11,
  fontWeight: 600,
  border: '1px solid #223046',
  background: '#141f31',
  color: '#dce5f2',
};

const Toggle: React.FC<{ label: string; on: boolean; disabled?: boolean; onClick: () => void; id: string }> = ({
  label,
  on,
  disabled,
  onClick,
  id,
}) => (
  <div style={chip}>
    <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.05em', color: on ? '#6ee7b7' : '#f59e0b' }}>
      {label} {on ? 'ON' : 'OFF'}
    </span>
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={on}
      onClick={onClick}
      disabled={disabled}
      style={{
        position: 'relative',
        height: 16,
        width: 32,
        borderRadius: 999,
        background: on ? '#10b981' : '#334155',
        transition: '.15s',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: on ? 18 : 2,
          height: 12,
          width: 12,
          borderRadius: 999,
          background: '#fff',
          transition: '.15s',
        }}
      />
    </button>
  </div>
);

const MapHeader: React.FC<MapHeaderProps> = ({
  currentUser,
  headerClock,
  showInterventionSectors,
  setShowInterventionSectors,
  priseAppels,
  priseLoadError,
  priseToggling,
  togglePriseAppels,
  navigate,
  hasPanelEntryAccess,
}) => (
  <header
    className="absolute inset-x-0 top-0 z-[1200] flex items-center justify-between gap-4 px-3"
    style={{
      height: TOPBAR_HEIGHT,
      background: 'linear-gradient(180deg,#16223a 0%,#0a111d 100%)',
      borderBottom: '1px solid #223046',
      boxShadow: '0 6px 24px rgba(0,0,0,.5)',
    }}
  >
    <div className="flex items-center gap-3">
      <Link to="/" className="flex items-center gap-2">
        <img src="/images/logo.png" alt="Logo" className="h-7 w-7 rounded-sm object-contain" />
        <div className="leading-none">
          <p style={{ fontSize: 8, letterSpacing: '.22em', color: '#6b7d95' }} className="uppercase">
            Centre de commandement
          </p>
          <p style={{ fontSize: 13, fontWeight: 800, letterSpacing: '.04em', color: '#fff' }}>OPSECOURS WEB</p>
        </div>
      </Link>
      <div className="flex items-center gap-2 pl-3" style={{ borderLeft: '1px solid #223046' }}>
        <span style={{ ...chip, color: '#6ee7b7', borderColor: '#134e3a', background: '#0b3d31' }}>
          € {Number(currentUser.argents ?? 0).toLocaleString('fr-FR')}
        </span>
        <span style={{ ...chip, color: '#fcd34d', borderColor: '#4a3a12', background: '#3b2a08' }}>
          🏅 {Number(currentUser.medailles ?? 0).toLocaleString('fr-FR')}
        </span>
      </div>
    </div>

    <div className="flex min-w-0 items-center gap-2 overflow-x-auto">
      <time className="hidden shrink-0 sm:block" style={{ fontSize: 11, color: '#9fb0c6', fontFamily: 'monospace' }}>
        {headerClock}
      </time>

      <Toggle
        id="map-toggle-sectors"
        label="Secteurs"
        on={showInterventionSectors}
        onClick={() =>
          setShowInterventionSectors((prev) => {
            const next = !prev;
            localStorage.setItem('mapShowInterventionSectors', next ? 'true' : 'false');
            return next;
          })
        }
      />
      <Toggle
        id="map-toggle-prise-appels"
        label="Appels"
        on={priseAppels}
        disabled={priseToggling}
        onClick={() => void togglePriseAppels()}
      />

      <button type="button" onClick={() => navigate('/dashboard')} style={{ ...chip }} className="hover:brightness-125">
        Dashboard
      </button>

      {hasPanelEntryAccess && (
        <button
          type="button"
          onClick={() => openAppRouteInNewWindow('/panel')}
          style={{ ...chip, color: '#c7d2fe', borderColor: '#3730a3', background: '#1e1b4b' }}
          className="hover:brightness-125"
        >
          Panel
        </button>
      )}

      <span style={{ ...chip, color: '#6ee7b7', borderColor: '#134e3a', background: 'rgba(16,185,129,.12)' }} className="uppercase">
        {currentUser.username} · en ligne
      </span>

      {priseLoadError && <span className="hidden xl:inline" style={{ fontSize: 10, color: '#fca5a5' }}>{priseLoadError}</span>}
    </div>
  </header>
);

export default MapHeader;
